import { useEffect, useState, type ReactElement } from "react";

import { api } from "../api/client";

// Client-side throttle between resends (the server also rate-limits). Mostly to
// stop accidental double-taps and set the "give it a few minutes" expectation.
const COOLDOWN_SECONDS = 300;

function mmss(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * A button that re-requests the email-verification link for `email`. Shown on the
 * registration confirmation screen and on the login screen when an unverified
 * account is refused. After a send it enters a 5-minute cooldown. The endpoint
 * responds identically for any address, so there is nothing to leak here.
 */
export function ResendVerification({ email }: { email: string }): ReactElement {
  const [state, setState] = useState<"idle" | "sending" | "error">("idle");
  const [sent, setSent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Tick the cooldown down once per second until it reaches zero.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = window.setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
    return () => window.clearTimeout(id);
  }, [secondsLeft]);

  async function resend(): Promise<void> {
    setState("sending");
    try {
      await api("POST", "/auth/verify-email/resend", { json: { email }, auth: false });
      setState("idle");
      setSent(true);
      setSecondsLeft(COOLDOWN_SECONDS);
    } catch {
      setState("error");
    }
  }

  const cooling = secondsLeft > 0;
  const label =
    state === "sending"
      ? "Sending…"
      : cooling
        ? `Resend available in ${mmss(secondsLeft)}`
        : "Resend verification email";

  return (
    <>
      {sent ? (
        <p className="muted">Verification email sent. Check your inbox (and spam).</p>
      ) : null}
      <button
        type="button"
        className="btn-ghost btn-block"
        onClick={resend}
        disabled={state === "sending" || cooling || !email}
      >
        {label}
      </button>
      {state === "error" ? (
        <p className="form-error">Couldn&apos;t send just now. Please try again shortly.</p>
      ) : null}
    </>
  );
}
